import { createReducer, on } from '@ngrx/store';

import { ServiceTicketActions } from './service-ticket.actions';
import { ServiceTicketHelper } from './service-ticket.helper';
import {
  ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN,
  ServiceTicket,
  ServiceTicketBoilerSerialErrors,
  ServiceTicketCallType,
} from './service-ticket.model';
import { ServiceTicketState } from './service-ticket.state';

export const initServiceTicketState: ServiceTicketState = {
  activePage: ServiceTicket.ServiceTicketPage.Create,
  arrangedCallbacks: [],
  createPageState: {
    searchedSerialNumber: null,
    searchedSerialMarketingName: null,
    bypassBoilerSerialValidation: false,
    searchedSerialNumberValidationState: {
      isValid: null,
      loading: false,
      loaded: false,
      error: null,
    },
    deviceDetails: {
      errorsDetails: null,
      loading: false,
      loaded: false,
      error: null,
    },
    routingProviderState: {
      routingProvider: null,
      loading: false,
      loaded: false,
      error: null,
    },
    userErrorCodes: null,
    otherErrorSelected: null,
    userOtherErrorCodes: null,
    isNoErrorCode: null,
    allFetchedErrors: [],
  },
  callPageState: {
    feedback: null,
    feedbackUpdating: null,
    callType: ServiceTicketCallType.CallNow,
    customerNumber: null,
    customerNumberPrefix: null,
    phoneNumber: null,
    isValidPhoneNumber: null,
    waitingTimeState: {
      waitTime: null,
      loading: false,
      loaded: false,
      error: null,
    },
    callNowTimeExpired: null,
    callNowState: {
      acdNumber: null,
      loading: false,
      loaded: false,
      error: null,
    },
    arrangedCallState: {
      arrangedCallback: null,
      loading: false,
      loaded: false,
      error: null,
    },
  },
  articlesPageState: {
    selectedArticleId: null,
    searchedArticlesState: {
      loading: false,
      loaded: false,
    },
  },
};

export const serviceTicketReducer = createReducer(
  initServiceTicketState,

  on(
    ServiceTicketActions.updateSearchedBoilerSerial.attempt,
    (state, { boilerSerial }): ServiceTicketState => ({
      ...initServiceTicketState,
      arrangedCallbacks: state.arrangedCallbacks,
      createPageState: {
        ...initServiceTicketState.createPageState,
        searchedSerialNumber: boilerSerial,
        searchedSerialNumberValidationState: {
          isValid: null,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.navigation.close,
    (state): ServiceTicketState => {
      return {
        ...initServiceTicketState,
        arrangedCallbacks: state.arrangedCallbacks,
      };
    }
  ),

  on(
    ServiceTicketActions.navigation.openCallPage,
    (state): ServiceTicketState => {
      return {
        ...state,
        activePage: ServiceTicket.ServiceTicketPage.Call,
      };
    }
  ),

  on(
    ServiceTicketActions.navigation.openArticlesPage,
    (state): ServiceTicketState => {
      return {
        ...state,
        activePage: ServiceTicket.ServiceTicketPage.Articles,
      };
    }
  ),

  on(
    ServiceTicketActions.navigation.navigateBackToCreatePage,
    (state): ServiceTicketState => {
      return {
        ...state,
        activePage: ServiceTicket.ServiceTicketPage.Create,
        callPageState: {
          ...initServiceTicketState.callPageState,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.routingProviderRequest.attempt,
    (state): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          routingProviderState: {
            routingProvider: null,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }
  ),

  on(
    ServiceTicketActions.routingProviderRequest.success,
    (state, { routingProvider }): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          routingProviderState: {
            routingProvider,
            loading: false,
            loaded: true,
            error: null,
          },
        },
        callPageState: {
          ...state.callPageState,
          callType: ServiceTicketHelper.getCallType(routingProvider),
        },
      };
    }
  ),

  on(
    ServiceTicketActions.routingProviderRequest.error,
    (state, { error }): ServiceTicketState => {
      const routingProvider = state.createPageState.routingProviderState.routingProvider;

      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          routingProviderState: {
            routingProvider: null,
            loading: false,
            loaded: true,
            error,
          },
        },
        callPageState: {
          ...state.callPageState,
          callType: ServiceTicketHelper.getCallType(routingProvider, error),
        },
      };
    }
  ),

  on(
    ServiceTicketActions.updateMarketingName.success,
    (state, { marketingName }): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          searchedSerialMarketingName: marketingName,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.updateSelectedErrorCodes.attempt,
    (state, { errorCodes, otherErrorSelected }): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          userErrorCodes: errorCodes,
          userOtherErrorCodes: otherErrorSelected
            ? state.createPageState.userOtherErrorCodes
            : null,
          otherErrorSelected,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.updateNoErrorCodeSelection.attempt,
    (state, { isNoErrorCode }): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        isNoErrorCode,
      },
    })
  ),

  on(
    ServiceTicketActions.validateBoilerSerial.attempt,
    (state): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        searchedSerialNumberValidationState: {
          isValid: null,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.validateBoilerSerial.success,
    (state): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        searchedSerialNumberValidationState: {
          isValid: true,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.validateBoilerSerial.error,
    (state, { error }): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        searchedSerialNumberValidationState: {
          isValid: false,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.validateBoilerSerial.gatewaySerialInsteadOfBoilerError,
    (state): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        searchedSerialNumberValidationState: {
          isValid: false,
          loading: false,
          loaded: true,
          error: ServiceTicketBoilerSerialErrors.GatewaySerialInsteadOfBoilerSerial,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.bypassBoilerSerialValidation.attempt,
    (state): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        bypassBoilerSerialValidation: true,
        searchedSerialNumberValidationState: {
          isValid: true,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.loadBoilerDeviceDetails.attempt,
    (state): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        deviceDetails: {
          errorsDetails: null,
          loading: true,
          loaded: false,
          error: null,
        },
        allFetchedErrors: [],
      },
    })
  ),

  on(
    ServiceTicketActions.loadBoilerDeviceDetails.success,
    (state, { errorsDetails }): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        otherErrorSelected: errorsDetails?.details?.length === 0 ? true : false,
        deviceDetails: {
          errorsDetails,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.loadBoilerDeviceDetails.error,
    (state, { error }): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          otherErrorSelected: true,
          deviceDetails: {
            errorsDetails: null,
            loading: false,
            loaded: true,
            error,
          },
        },
      };
    }
  ),

  on(
    ServiceTicketActions.loadBoilerDeviceDetails.loadErrors,
    (state, { allErrors }): ServiceTicketState => {
      return {
        ...state,
        createPageState: {
          ...state.createPageState,
          allFetchedErrors: allErrors.faultCodes,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.updateOtherErrorCode.loadDocuments,
    (state, { otherErrorCode }): ServiceTicketState => ({
      ...state,
      createPageState: {
        ...state.createPageState,
        userOtherErrorCodes: otherErrorCode,
      },
      articlesPageState: {
        ...state.articlesPageState,
        searchedArticlesState: {
          loading: true,
          loaded: false,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.updateOtherErrorCode.onDocumentsLoaded,
    (state): ServiceTicketState => ({
      ...state,
      articlesPageState: {
        ...state.articlesPageState,
        searchedArticlesState: {
          loading: false,
          loaded: true,
        },
      },
    })
  ),

  // Page Call

  on(
    ServiceTicketActions.updateFeedback.attempt,
    (state, { feedback }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...initServiceTicketState.callPageState,
        feedback,
        feedbackUpdating: true,
      },
    })
  ),

  on(
    ServiceTicketActions.updateFeedback.success,
    (state): ServiceTicketState => {
      const error = state.createPageState.routingProviderState?.error;
      const routingProvider = state.createPageState.routingProviderState?.routingProvider;

      return {
        ...state,
        callPageState: {
          ...state.callPageState,
          callType: ServiceTicketHelper.getCallType(routingProvider, error),
          feedbackUpdating: false,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.updatePhoneNumber.attempt,
    (state, { phoneNumber, isValidPhoneNumber }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        phoneNumber,
        isValidPhoneNumber,
      },
    })
  ),

  on(
    ServiceTicketActions.updateCallType.attempt,
    (state, { callType }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        callType,
        waitingTimeState: {
          ...initServiceTicketState.callPageState.waitingTimeState,
        },
        arrangedCallState: {
          ...initServiceTicketState.callPageState.arrangedCallState,
        },
        callNowTimeExpired: null,
        callNowState: {
          ...initServiceTicketState.callPageState.callNowState,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.requestWaitingTime.attempt,
    (state): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        waitingTimeState: {
          waitTime: null,
          loading: true,
          loaded: false,
          error: null,
        },
        arrangedCallState: {
          ...initServiceTicketState.callPageState.arrangedCallState,
        },
        callNowTimeExpired: null,
        callNowState: {
          ...initServiceTicketState.callPageState.callNowState,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.requestWaitingTime.success,
    (state, { waitTime }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        waitingTimeState: {
          waitTime,
          loading: false,
          loaded: true,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.requestWaitingTime.error,
    (state, { error }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        waitingTimeState: {
          waitTime: null,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.callNowRequest.attempt,
    (state): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        callNowState: {
          acdNumber: null,
          loading: true,
          loaded: false,
          error: null,
        },
        callNowTimeExpired: null,
      },
    })
  ),

  on(
    ServiceTicketActions.callNowRequest.success,
    (state, { acdNumber }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        callNowState: {
          acdNumber: acdNumber,
          loading: false,
          loaded: true,
          error: null,
        },
        callNowTimeExpired: false,
      },
    })
  ),

  on(
    ServiceTicketActions.callNowRequest.error,
    (state, { error }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        callNowState: {
          acdNumber: null,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.callNowTimeExpired.attempt,
    (state): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        callNowTimeExpired: true,
      },
    })
  ),

  on(
    ServiceTicketActions.arrangedCallbackRequest.attempt,
    (state): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        arrangedCallState: {
          arrangedCallback: null,
          loading: true,
          loaded: false,
          error: null,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.removeCachedCallback.attempt,
    (state, { id }): ServiceTicketState => {
      const _arrangedCallbacks = state.arrangedCallbacks.filter((entry) => entry.id !== id);
      localStorage.setItem(
        ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN,
        JSON.stringify(_arrangedCallbacks)
      );

      return {
        ...state,
        arrangedCallbacks: _arrangedCallbacks,
        callPageState: {
          ...state.callPageState,
        },
      };
    }
  ),

  on(
    ServiceTicketActions.arrangedCallbackRequest.success,
    (state, { arrangedCallback }): ServiceTicketState => {
      const _arrangedCallbacks = state.arrangedCallbacks.concat([arrangedCallback]);
      localStorage.setItem(
        ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN,
        JSON.stringify(_arrangedCallbacks)
      );

      return {
        ...state,
        arrangedCallbacks: _arrangedCallbacks,
        callPageState: {
          ...state.callPageState,
          arrangedCallState: {
            arrangedCallback,
            loading: false,
            loaded: true,
            error: null,
          },
        },
      };
    }
  ),

  on(
    ServiceTicketActions.arrangedCallbackRequest.error,
    (state, { error }): ServiceTicketState => ({
      ...state,
      callPageState: {
        ...state.callPageState,
        arrangedCallState: {
          arrangedCallback: null,
          loading: false,
          loaded: true,
          error,
        },
      },
    })
  ),

  on(
    ServiceTicketActions.loadCachedCallbacks.success,
    (state, { cachedCallbacks }): ServiceTicketState => {
      const _arrangedCallbacks = state.arrangedCallbacks.concat(cachedCallbacks);

      localStorage.setItem(
        ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN,
        JSON.stringify(_arrangedCallbacks)
      );

      return {
        ...state,
        arrangedCallbacks: _arrangedCallbacks,
      };
    }
  )
);
